/**
 * ListenerManager - Prevents excessive Firestore reads from orphaned tabs
 *
 * Features:
 * - Auto-disconnect listeners after 30 min of user inactivity
 * - Disconnect listeners after 10 min when tab is hidden
 * - Track all active listeners with source information
 * - Tab visibility detection
 * - Debugging and monitoring tools
 */

class ListenerManager {
  constructor() {
    this.listeners = new Map(); // Map<listenerId, listenerInfo>
    this.lastActivityTime = Date.now();
    this.isTabVisible = !document.hidden;
    this.tabHiddenTime = null;

    // Configuration
    this.INACTIVITY_TIMEOUT = 30 * 60 * 1000; // 30 minutes
    this.TAB_HIDDEN_TIMEOUT = 10 * 60 * 1000; // 10 minutes
    this.CHECK_INTERVAL = 60 * 1000; // Check every minute

    // Start monitoring
    this.setupActivityListeners();
    this.setupVisibilityListener();
    this.startCleanupTimer();

    console.log('🎧 ListenerManager initialized');
  }

  /**
   * Register a new Firestore listener
   * @param {string} source - Description of where the listener is from (e.g., "useDashboardStats")
   * @param {Function} unsubscribe - The unsubscribe function from onSnapshot
   * @param {Object} metadata - Additional info (collection, query details, etc.)
   * @returns {string} Unique listener ID
   */
  registerListener(source, unsubscribe, metadata = {}) {
    const listenerId = `${source}-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

    this.listeners.set(listenerId, {
      id: listenerId,
      source,
      unsubscribe,
      metadata,
      registeredAt: Date.now(),
      lastActiveAt: Date.now(),
      status: 'active'
    });

    console.log(`🎧 Registered listener: ${source} (total: ${this.listeners.size})`);

    return listenerId;
  }

  /**
   * Manually unregister a listener
   * @param {string} listenerId - The listener ID to remove
   */
  unregisterListener(listenerId) {
    const listener = this.listeners.get(listenerId);

    if (listener) {
      try {
        listener.unsubscribe();
        console.log(`🎧 Unregistered listener: ${listener.source}`);
      } catch (error) {
        console.error(`Error unsubscribing listener ${listenerId}:`, error);
      }

      this.listeners.delete(listenerId);
    }
  }

  /**
   * Disconnect all listeners
   */
  disconnectAll() {
    console.log(`🎧 Disconnecting all ${this.listeners.size} listeners`);

    this.listeners.forEach((listener, id) => {
      try {
        listener.unsubscribe();
      } catch (error) {
        console.error(`Error disconnecting listener ${id}:`, error);
      }
    });

    this.listeners.clear();
  }

  /**
   * Get all active listeners (for debugging/monitoring)
   * @returns {Array} Array of listener info objects
   */
  getActiveListeners() {
    return Array.from(this.listeners.values()).map(listener => ({
      id: listener.id,
      source: listener.source,
      metadata: listener.metadata,
      registeredAt: listener.registeredAt,
      durationMs: Date.now() - listener.registeredAt,
      status: listener.status
    }));
  }

  /**
   * Get summary statistics
   * @returns {Object} Stats about active listeners
   */
  getStats() {
    const listeners = this.getActiveListeners();
    const now = Date.now();

    return {
      totalListeners: this.listeners.size,
      isTabVisible: this.isTabVisible,
      lastActivityTime: this.lastActivityTime,
      timeSinceActivity: now - this.lastActivityTime,
      tabHiddenTime: this.tabHiddenTime,
      timeSinceHidden: this.tabHiddenTime ? now - this.tabHiddenTime : null,
      listenersBySource: this.groupListenersBySource(listeners),
      oldestListener: listeners.length > 0
        ? Math.max(...listeners.map(l => l.durationMs))
        : 0
    };
  }

  /**
   * Setup activity listeners to track user interaction
   * @private
   */
  setupActivityListeners() {
    const updateActivity = () => {
      this.lastActivityTime = Date.now();

      // If tab is visible and user is active, update all listeners
      if (this.isTabVisible) {
        this.listeners.forEach(listener => {
          listener.lastActiveAt = Date.now();
        });
      }
    };

    // Track various user interactions
    ['mousedown', 'keydown', 'scroll', 'touchstart', 'click'].forEach(event => {
      window.addEventListener(event, updateActivity, { passive: true });
    });
  }

  /**
   * Setup visibility change listener
   * @private
   */
  setupVisibilityListener() {
    document.addEventListener('visibilitychange', () => {
      this.isTabVisible = !document.hidden;

      if (document.hidden) {
        this.tabHiddenTime = Date.now();
        console.log(`🎧 Tab hidden - listeners will disconnect in ${this.TAB_HIDDEN_TIMEOUT / 60000} minutes`);
      } else {
        this.tabHiddenTime = null;
        this.lastActivityTime = Date.now();
        console.log(`🎧 Tab visible - listeners reactivated`);
      }
    });
  }

  /**
   * Start the cleanup timer that checks for inactive listeners
   * @private
   */
  startCleanupTimer() {
    setInterval(() => {
      this.checkAndCleanupListeners();
    }, this.CHECK_INTERVAL);
  }

  /**
   * Check for inactive listeners and clean them up
   * @private
   */
  checkAndCleanupListeners() {
    const now = Date.now();
    const inactivityThreshold = now - this.INACTIVITY_TIMEOUT;
    const hiddenThreshold = this.tabHiddenTime
      ? this.tabHiddenTime + this.TAB_HIDDEN_TIMEOUT
      : null;

    let disconnectedCount = 0;

    // Check for inactive listeners
    if (this.lastActivityTime < inactivityThreshold) {
      console.log(`🎧 User inactive for ${Math.round((now - this.lastActivityTime) / 60000)} minutes - disconnecting all listeners`);
      this.disconnectAll();
      return;
    }

    // Check for hidden tab timeout
    if (hiddenThreshold && now > hiddenThreshold && this.listeners.size > 0) {
      console.log(`🎧 Tab hidden for ${Math.round((now - this.tabHiddenTime) / 60000)} minutes - disconnecting all listeners`);
      this.disconnectAll();
      return;
    }

    // Log status periodically
    if (this.listeners.size > 0) {
      const stats = this.getStats();
      console.log(`🎧 ListenerManager status: ${stats.totalListeners} active listeners, tab ${stats.isTabVisible ? 'visible' : 'hidden'}`);
    }
  }

  /**
   * Group listeners by source for stats
   * @private
   */
  groupListenersBySource(listeners) {
    return listeners.reduce((acc, listener) => {
      acc[listener.source] = (acc[listener.source] || 0) + 1;
      return acc;
    }, {});
  }

  /**
   * Force reconnect all listeners (useful after coming back from hidden state)
   */
  reconnectAll() {
    console.log('🎧 Reconnect all is not implemented - components should re-mount their listeners');
    // Note: We can't automatically reconnect because we don't have the query details
    // Components should handle this in their useEffect hooks
  }
}

// Create singleton instance
export const listenerManager = new ListenerManager();

// Export for testing or advanced usage
export default ListenerManager;
